import React, { useEffect, useState, useRef, useCallback } from "react";
import { Check, Trash2, Search } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getServices,
  getClientServiceGroups,
  updateService,
  clearError,
  clearListings,
  createService,
  deleteService,
} from "../../store/slices/servicesSlice";
import { withVersion } from "../../utils/concurrency";
import {
  CreateService,
  Service,
  UpdateService,
  ServiceFilters as Filters,
} from "../../types/service";
import Button from "../../components/UI/Button";
import { Modal } from "../../components/UI/Modal";
import { ServiceForm } from "../../components/services/ServiceForm";
import { ServiceTable } from "../../components/services/ServicesTable";
import { ClientServicesTable } from "../../components/services/ClientServicesTable";
import { Pagination } from "../../components/ListView/Pagination";
import { Input } from "../../components/UI/FormField";
import { DirectorySelect } from "../../components/directory/DirectorySelect";
import { apiResource } from "../../services/api";

/** The listing is split in two: the shared rate card, and per-client rates. */
type ServiceTab = "general" | "client";

const TABS: ServiceTab[] = ["general", "client"];

export const ServicesListing: React.FC = () => {
  const { t } = useTranslation(["services", "common"]);
  const dispatch = useAppDispatch();
  const {
    services,
    clientGroups,
    clientGroupsSearch,
    clientGroupsPagination,
    loading,
    error,
    pagination,
  } = useAppSelector((state) => state.services);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<Filters>({
    search: "",
    page: 1,
    limit: 20,
  });
  const [modalServiceType, setModalServiceType] = useState<string | null>(null);
  /** Which half of the directory is on screen: the shared rate card, or the
   *  per-client overrides of it. */
  const [tab, setTab] = useState<ServiceTab>("general");
  /** Narrows the client tab to one client; null means all of them. */
  const [clientFilter, setClientFilter] = useState<string | null>(null);
  /** DirectorySelect is told what to show, so the name lives here beside the id. */
  const [clientFilterName, setClientFilterName] = useState("");

  const prevSearch = useRef(filters.search);

  // One place that turns tab + filters into a request, so the list effect and
  // the post-mutation refetches can never drift apart.
  const listRequest = useCallback(() => {
    const base = {
      search: filters.search,
      page: filters.page,
      limit: filters.limit,
    };

    // The two tabs are two listings, not one listing filtered two ways:
    // General pages over the shared rate card, while Client Specific pages
    // over clients and hands each one the rates it prices.
    return tab === "general"
      ? getServices({ ...base, partnerId: "GENERAL" })
      : getClientServiceGroups(
          clientFilter ? { ...base, partnerId: clientFilter } : base,
        );
  }, [filters, tab, clientFilter]);

  // Refetch the current view after a create/update/delete, so the list never
  // shows a row that no longer belongs in this tab (or misses one that does).
  const refetch = useCallback(
    () => dispatch(listRequest()),
    [dispatch, listRequest],
  );

  useEffect(() => {
    const searchChanged = prevSearch.current !== filters.search;
    prevSearch.current = filters.search;

    const run = () => dispatch(listRequest());

    if (searchChanged && filters.search !== "") {
      const timer = setTimeout(run, 750);
      return () => clearTimeout(timer);
    }
    run();
  }, [dispatch, filters, tab, clientFilter, listRequest]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleFiltersChange = (newFilters: Partial<Filters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: newFilters.page || 1,
    }));
    setSelectedIds([]);
  };

  const handlePageChange = (page: number) => handleFiltersChange({ page });

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? services.map((s) => s._id) : []);
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((sid) => sid !== id),
    );
  };

  const handleTabChange = (next: ServiceTab) => {
    if (next === tab) return;
    setTab(next);
    // Each tab arrives unfiltered. The two are separate listings — one pages
    // over the shared rate card, the other over clients — so a search or a
    // picked client carried across would narrow the new tab by something the
    // user typed about the old one, with an empty-looking result to show for
    // it. The client picker is not even on screen on the general tab.
    setClientFilter(null);
    setClientFilterName("");
    setSelectedIds([]);
    setFilters((prev) => ({ ...prev, search: "", page: 1 }));
    // And it arrives empty rather than holding what this tab showed the last
    // time it was open — those rows answered a question nobody asked again.
    dispatch(clearListings());
  };

  /** Whether a service with this partner belongs in the tab on screen. */
  const isVisibleInTab = (partnerId?: string | null) =>
    tab === "general"
      ? !partnerId
      : !!partnerId && (!clientFilter || partnerId === clientFilter);

  const handleCreateService = async (data: CreateService) => {
    try {
      const created = await dispatch(createService(data)).unwrap();
      setIsModalOpen(false);

      // The form can create either kind regardless of the tab in view, so say
      // where the row went instead of leaving the user hunting for it.
      if (isVisibleInTab(created?.partnerId)) {
        toast.success(t("services:messages.createdSuccess"));
      } else {
        toast.info(
          t(
            created?.partnerId
              ? "services:messages.createdHiddenInGeneral"
              : "services:messages.createdHiddenInClients",
          ),
        );
      }

      refetch();
    } catch (error) {
      console.error(error);
      toast.error(
        typeof error === "string" ? error : t("services:messages.createFailed"),
      );
    }
  };

  const handleUpdateService = async (data: Partial<Service>) => {
    if (!editingService) return;

    try {
      if (data.type === "MATRIX") {
        if (!data.zones || data.zones.length === 0) {
          toast.error(t("services:form.matrixRequiresZone"));
          return;
        }
        if (!data.bands || data.bands.length === 0) {
          toast.error(t("services:form.matrixRequiresBand"));
          return;
        }
        if (!data.priceMatrix || data.priceMatrix.length === 0) {
          toast.error(t("services:form.matrixRequiresPrices"));
          return;
        }

        const serviceInfo: UpdateService = {
          name: data.name,
          type: data.type,
          description: data.description,
          tax: data.tax,
          accounting: data.accounting,
          invoiceDetails: data.invoiceDetails,
          partnerId: data.partnerId,
          zones: data.zones,
          bands: data.bands,
          priceMatrix: data.priceMatrix,
        };

        await dispatch(
          updateService({
            id: editingService._id,
            serviceData: withVersion(serviceInfo, editingService),
          }),
        ).unwrap();
      } else {
        if (!data.name || !data.type) {
          toast.error(t("services:form.serviceCodeAndTypeRequired"));
          return;
        }

        const serviceInfo: UpdateService = {
          name: data.name,
          type: data.type,
          description: data.description,
          // `null`, never `undefined`. A blank price box means "this service
          // has no default rate" and has to REACH the server to clear a rate
          // that is already stored — the update is PATCH-shaped, so an absent
          // key means "leave it alone" and axios drops undefined keys
          // entirely. Collapsing null here would make clearing a price look
          // like it worked and silently keep the old figure.
          price: data.price ?? null,
          tax: data.tax,
          accounting: data.accounting,
          invoiceDetails: data.invoiceDetails,
          partnerId: data.partnerId,
        };

        await dispatch(
          updateService({
            id: editingService._id,
            serviceData: withVersion(serviceInfo, editingService),
          }),
        ).unwrap();
      }

      setIsModalOpen(false);
      setEditingService(null);

      toast.success(t("services:messages.updatedSuccess"));

      refetch();
    } catch (error) {
      console.error(error);
      toast.error(
        typeof error === "string" ? error : t("services:messages.updateFailed"),
      );
    }
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    setModalServiceType(service.type);
    setIsModalOpen(true);
  };

  /**
   * Retire a service.
   *
   * The server archives rather than deletes — a code is still what the charges
   * and invoice lines priced from it resolve against, so removing the row
   * silently blanked their accounting mapping. It refuses while uninvoiced work
   * still carries the code, and says what and how much; that refusal is worth
   * reading rather than retrying, so it is offered as a second confirmation
   * with the server's own wording instead of a generic failure.
   */
  const handleDelete = async (id: string, force = false) => {
    if (!force && !window.confirm(t("services:page.deleteConfirm"))) return;
    try {
      await dispatch(deleteService({ id, force })).unwrap();
      toast.success(t("services:messages.deletedSuccess"));
      setSelectedIds((prev) => prev.filter((sid) => sid !== id));
      refetch();
    } catch (error) {
      console.error(error);
      const message =
        typeof error === "string" ? error : t("services:messages.deleteFailed");
      // The server asked whether to go ahead anyway — archiving is safe for the
      // work that already references the code, it just stops being silent.
      if (!force && typeof error === "string" && error.includes("retire it anyway")) {
        if (window.confirm(message)) await handleDelete(id, true);
        return;
      }
      toast.error(message);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingService(null);
    setModalServiceType(null);
  };

  /**
   * A refetch running over rows that are already on screen.
   *
   * Those rows are kept, dimmed, rather than swapped out from under the
   * reader: the listing does not jump, and what is on screen reads as the old
   * answer being worked on rather than as the new one. A first load has
   * nothing to keep, so it still gets the skeleton.
   */
  const busy =
    loading &&
    (tab === "client" ? clientGroups.length > 0 : services.length > 0);

  // The client tab counts clients, the general tab counts services — each
  // listing is paged over its own rows.
  const { page, pages, total } =
    tab === "client" ? clientGroupsPagination : pagination;

  const paginationDetails = {
    page,
    pages,
    total,
    hasNext: page < pages,
    hasPrev: page > 1,
  };

  /**
   * Feeds the form's general-service picker. Deliberately a bare API call
   * rather than the getServices thunk: that thunk owns the list rendered
   * behind the modal, so dispatching it here would swap the table out from
   * under the user on every keystroke in the picker.
   */
  const fetchGeneralServices = useCallback(
    async (params: { search: string; page: number; limit: number }) => {
      const response = await apiResource("services").getAll({
        ...params,
        partnerId: "GENERAL",
      });
      return response.data.data as { services: Service[] };
    },
    [],
  );

  // Client tab only: narrow to one client, or back to all of them.
  const handleClientFilterChange = (
    partnerId: string | null,
    companyName: string,
  ) => {
    setClientFilter(partnerId);
    setClientFilterName(companyName);
    setSelectedIds([]);
    setFilters((prev) => ({ ...prev, page: 1 }));
    // Picking a client says which client to look at, so every other one goes
    // now rather than lingering, dimmed, until the request lands — the answer
    // to "show me this client" is never a screen of the others.
    dispatch(clearListings());
  };

  return (
    <div>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-dark-900 mb-4">
            {t("services:page.title")}
          </h1>

          <div
            role="tablist"
            aria-label={t("services:page.title")}
            className="flex gap-1 border-b border-gray-200 mb-4"
          >
            {TABS.map((key) => {
              const selected = tab === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => handleTabChange(key)}
                  className={`-mb-px border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                    selected
                      ? "border-[#C97A3C] text-[#0B0F0E]"
                      : "border-transparent text-[#8A9590] hover:text-[#5A6460]"
                  }`}
                >
                  {t(`services:page.tab.${key}`)}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-4 max-sm:flex-wrap">
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-[250px] max-w-[250px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <Input
                    type="text"
                    placeholder={t("services:page.search")}
                    value={filters.search}
                    onChange={(e) =>
                      handleFiltersChange({ search: e.target.value })
                    }
                    className="pl-10 bg-white w-[220px]"
                  />
                </div>
              </div>

              {/* Only the client tab has clients to filter by — on the
                  general tab every row is client-less by definition. */}
              {tab === "client" && (
                <div className="w-[220px] max-w-[250px]">
                  <DirectorySelect
                    source="partners"
                    selectedId={clientFilter}
                    value={clientFilterName}
                    onSelect={(snapshot) =>
                      handleClientFilterChange(
                        snapshot._id,
                        snapshot.companyName,
                      )
                    }
                    // The cross is the way back to every client — there is no
                    // "all" row in the directory to pick.
                    onClear={() => handleClientFilterChange(null, "")}
                    size="sm"
                    placeholder={t("services:page.allClients")}
                    // Filtering by a client that is not in the directory yet is
                    // not a thing; adding one belongs on the Partners page.
                    allowCreate={false}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <Button onClick={() => setIsModalOpen(true)} variant="primary">
                {t("services:page.addService")}
              </Button>
            </div>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Check className="h-5 w-5 text-blue-600" />
              <span className="text-blue-800 font-medium">
                {t("services:page.itemsSelected", {
                  count: selectedIds.length,
                })}
              </span>
            </div>
            <button className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
              <Trash2 className="h-4 w-4" />
              <span>{t("services:page.deleteSelected")}</span>
            </button>
          </div>
        )}

        <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {busy && (
            <div
              className="absolute inset-x-0 top-0 z-20 h-0.5 animate-pulse bg-primary"
              role="status"
              aria-label={t("common:labels.loading")}
            />
          )}

          <div
            className={`transition-opacity duration-200 ${
              busy ? "pointer-events-none opacity-60" : ""
            }`}
          >
            {tab === "client" ? (
              <ClientServicesTable
                groups={clientGroups}
                loading={loading}
                search={clientGroupsSearch}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ) : (
              <ServiceTable
                services={services}
                selectedIds={selectedIds}
                onSelectAll={handleSelectAll}
                onSelectOne={handleSelectOne}
                onEdit={handleEdit}
                onDelete={handleDelete}
                loading={loading}
              />
            )}
            <Pagination
              pagination={paginationDetails}
              onPageChange={handlePageChange}
              pageSize={filters.limit}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={
            editingService
              ? `${t("services:page.idPrefix")}${editingService.serviceId}`
              : t("services:page.addNewService")
          }
          size={modalServiceType === "MATRIX" ? "xl" : "lg"}
        >
          <div className="flex-1 overflow-y-auto">
            <ServiceForm
              service={editingService || undefined}
              onSubmit={(data) => {
                if (editingService) {
                  return handleUpdateService(data as any);
                } else {
                  return handleCreateService(data as CreateService);
                }
              }}
              loading={loading}
              isEditMode={!!editingService}
              onTypeChange={(type) => setModalServiceType(type)}
              fetchGeneralServices={fetchGeneralServices}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};
